import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, Grid, Divider, Chip, Alert, CircularProgress, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const REGIONS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];

const SUGGESTIONS = [
    'caneta esferográfica azul', 'papel A4 75g', 'serviço de limpeza', 'resma de papel',
    'toner para impressora', 'cadeira ergonômica', 'notebook', 'serviço de vigilância'
];

const SearchPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [step, setStep] = useState(1); // 1: Initial, 2: Catalog Discovery
    const [form, setForm] = useState({
        query: searchParams.get('query') || '',
        itemType: searchParams.get('itemType') || 'material',
        startDate: '', endDate: '', region: '', agency: '', quantity: '', unit: ''
    });
    const [catalogItems, setCatalogItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Local filters for the catalog discovery
    const [localFilters, setLocalFilters] = useState({ pdm: 'all', unit: 'all' });

    const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

    const handleInitialSearch = async (e) => {
        if (e) e.preventDefault();
        setError('');
        const q = form.query.trim();
        if (!q) { setError('Descreva o item ou insira um código'); return; }

        // If it's a direct CATMAT code (5-7 digits) or CATSER (higher), go straight to prices
        if (/^\d{5,10}$/.test(q)) {
            const params = new URLSearchParams();
            params.set('catmatCode', q);
            params.set('itemType', form.itemType);
            const listId = searchParams.get('listId');
            const itemId = searchParams.get('itemId');
            if (listId) params.set('listId', listId);
            if (itemId) params.set('itemId', itemId);
            navigate(`/resultados?${params.toString()}`);
            return;
        }

        setLoading(true);
        try {
            const res = await api.get(`/search/catalog?query=${q}&itemType=${form.itemType}`);
            if (res.data.success) {
                setCatalogItems(res.data.data);
                if (res.data.data.length === 0) {
                    setError('Nenhum item encontrado no catálogo com esta descrição.');
                } else {
                    setStep(2);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Falha ao consultar catálogo. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const filteredCatalog = (catalogItems || []).filter(item => {
        const pdmMatch = localFilters.pdm === 'all' || item.desc === localFilters.pdm;
        const chars = item.characteristics || [];
        const unitMatch = localFilters.unit === 'all' || chars.some(c => c.unit === localFilters.unit);
        return pdmMatch && unitMatch;
    });

    const uniquePdms = [...new Set((catalogItems || []).map(i => i.desc))];
    const uniqueUnits = [...new Set((catalogItems || []).flatMap(i => (i.characteristics || []).map(c => c.unit)).filter(u => u))];

    const handleSelectItem = (item) => {
        const params = new URLSearchParams();
        Object.entries(form).forEach(([k, v]) => { if (v) params.set(k, v); });
        params.set('catmatCode', item.code);
        params.set('query', item.summary || item.desc); // Use aggregate description if available

        // Preserve list context if exists
        const listId = searchParams.get('listId');
        const itemId = searchParams.get('itemId');
        if (listId) params.set('listId', listId);
        if (itemId) params.set('itemId', itemId);

        navigate(`/resultados?${params.toString()}`);
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={800} color="primary" gutterBottom>
                    Pesquisa de Preços
                </Typography>
                <Typography variant="h6" color="text.secondary">
                    {step === 1 ? 'Encontre o item oficial no catálogo para iniciar sua cotação' : `Resultados do Catálogo para: "${form.query}"`}
                </Typography>
            </Box>

            {step === 1 ? (
                <Grid container spacing={4}>
                    <Grid item xs={12} md={12} width={'100%'}>
                        <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 3 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box component="form" onSubmit={handleInitialSearch} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                                        <Box xs={12} sm={6} md={8}>
                                            <Typography variant="h6" fontWeight={700} gutterBottom>O que você deseja cotar hoje?</Typography>
                                            <TextField
                                                placeholder="Ex: caneta esferográfica, papel A4, 629168, 26867..."
                                                value={form.query}
                                                onChange={e => { set('query')(e); setError(''); }}
                                                fullWidth
                                                size="large"
                                                helperText="Dica: Digite o nome do material ou o código CATMAT/CATSER (ex: 629168)."
                                                InputProps={{
                                                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                                                    sx: { borderRadius: 2, fontSize: '1.1rem' }
                                                }}
                                            />
                                        </Box>

                                        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

                                        <Grid item xs={12} sm={3} md={3} sx={{ mt: 1.5 }}>
                                            <FormControl fullWidth>
                                                <InputLabel>Tipo de Item</InputLabel>
                                                <Select value={form.itemType} onChange={set('itemType')} label="Tipo de Item" sx={{ borderRadius: 2 }}>
                                                    <MenuItem value="material">Material (CATMAT)</MenuItem>
                                                    <MenuItem value="service">Serviço (CATSER)</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={3} md={3} sx={{ mt: 1 }}>
                                            <Button type="submit" variant="contained" fullWidth size="large" sx={{ height: 56, borderRadius: 2, fontSize: '1rem', fontWeight: 700 }} disabled={loading}>
                                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Consultar Catálogo'}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Box sx={{ mt: 4 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                                        Consultas Frequentes
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {SUGGESTIONS.map(s => (
                                            <Chip key={s} label={s} onClick={() => { setForm(f => ({ ...f, query: s })); }} variant="outlined" sx={{ cursor: 'pointer' }} />
                                        ))}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/*<Grid item xs={12} md={4}>
                        <Box sx={{ p: 2, bgcolor: 'rgba(7, 73, 96, 0.05)', borderRadius: 3, border: '1px dashed #074960' }}>
                            <Typography variant="h6" fontWeight={700} color="primary" gutterBottom>Como funciona?</Typography>
                            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {[
                                    { step: 1, title: 'Identificação', desc: 'Cruzamos sua descrição com o catálogo oficial CNBS/Serpro.' },
                                    { step: 2, title: 'Refino', desc: 'Você escolhe o item exato e aplica filtros de região e período.' },
                                    { step: 3, title: 'Cotação', desc: 'Buscamos preços reais no PNCP e Compras.gov.br.' }
                                ].map(item => (
                                    <Box key={item.step} sx={{ display: 'flex', gap: 2 }}>
                                        <Box sx={{ minWidth: 28, height: 28, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>
                                            {item.step}
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={700}>{item.title}</Typography>
                                            <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid> */}
                </Grid>
            ) : (
                <Grid container spacing={3}>
                    {/* Sidebar Filters */}
                    <Grid item xs={12} md={3}>
                        <Card sx={{ borderRadius: 3, position: 'sticky', top: 20 }}>
                            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={800} color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TuneIcon fontSize="small" /> FILTROS DO CATÁLOGO
                                    </Typography>

                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary" gutterBottom display="block">PADRÃO DESCRITIVO (PDM)</Typography>
                                        <FormControl fullWidth size="small">
                                            <Select value={localFilters.pdm} onChange={e => setLocalFilters(f => ({ ...f, pdm: e.target.value }))}>
                                                <MenuItem value="all">Todos os tipos ({uniquePdms.length})</MenuItem>
                                                {uniquePdms.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary" gutterBottom display="block">UNIDADE DE FORNECIMENTO</Typography>
                                        <FormControl fullWidth size="small">
                                            <Select value={localFilters.unit} onChange={e => setLocalFilters(f => ({ ...f, unit: e.target.value }))}>
                                                <MenuItem value="all">Todas as unidades</MenuItem>
                                                {uniqueUnits.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Box>

                                <Divider />
                                <Button variant="outlined" fullWidth onClick={() => { setStep(1); }} startIcon={<ArrowBackIcon />}>Nova Busca</Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Main Content */}
                    <Grid item xs={12} md={9}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                Foram encontrados <strong>{filteredCatalog.length}</strong> itens no catálogo para sua pesquisa. Selecione o mais adequado abaixo.
                            </Alert>

                            {filteredCatalog.map((item, idx) => (
                                <Card key={idx} sx={{ borderRadius: 3, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', borderColor: 'primary.main' }, border: '1px solid #eee' }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Grid container spacing={3} alignItems="center" justifyContent="space-between">
                                            <Grid item xs={12} md={9}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                                    <Chip label={item.code} color="primary" sx={{ fontWeight: 800, borderRadius: 1 }} />
                                                    <Typography variant="h6" fontWeight={700}>{item.desc}</Typography>
                                                </Box>

                                                <Grid container spacing={2}>
                                                    {(item.characteristics || []).slice(0, 6).map((c, i) => (
                                                        <Grid item xs={6} sm={4} key={i}>
                                                            <Typography variant="caption" color="text.secondary" display="block">{c.name}</Typography>
                                                            <Typography variant="body2" fontWeight={600} noWrap title={c.value}>{c.value} {c.unit || ''}</Typography>
                                                        </Grid>
                                                    ))}
                                                </Grid>

                                                <Box sx={{ mt: 2.5, display: 'flex', gap: 1 }}>
                                                    <Chip label={`NCM: ${item.ncm}`} size="small" variant="outlined" />
                                                    <Chip label={`PDM: ${item.pdmCode}`} size="small" variant="outlined" />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} md={3} sx={{ textAlign: { md: 'right' } }}>
                                                <Button variant="contained" fullWidth onClick={() => handleSelectItem(item)} sx={{ borderRadius: 2, fontWeight: 700, py: 1.5 }}>
                                                    Selecionar
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default SearchPage;
